import { ChangeDetectionStrategy, Component, inject, input, resource } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { StorageService } from '../../../core/services/storage.service';
import { Location } from '@angular/common';

@Component({
  selector: 'app-pdf-viewer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './pdf-viewer.html',
  styleUrl: './pdf-viewer.scss'
})
export class PdfViewerComponent {
  private readonly storageService = inject(StorageService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly snackBar = inject(MatSnackBar);
  private readonly location = inject(Location);

  public readonly filename = input.required<string>();

  public readonly pdfResource = resource({
    defaultValue: undefined as { url: string; safeUrl: SafeResourceUrl } | undefined,
    params: () => this.filename(),
    loader: async ({ params }): Promise<{ url: string; safeUrl: SafeResourceUrl }> => {
      try {
        const validatedFilename = this.validatePdfFilename(params);
        const url = await this.storageService.getDownloadUrl(`manuals/${validatedFilename}`);
        return {
          url,
          safeUrl: this.sanitizer.bypassSecurityTrustResourceUrl(url)
        };
      } catch (error: any) {
        console.error('Failed to get PDF URL:', error);
        this.snackBar.open('ファイルの読み込みに失敗しました', '閉じる', { duration: 5000 });
        throw error;
      }
    }
  });

  public goBack(): void {
    this.location.back();
  }

  private validatePdfFilename(filename: string): string {
    if (!filename) {
      throw new Error('PDF filename is empty.');
    }

    let decodedFilename: string;
    try {
      decodedFilename = decodeURIComponent(filename);
    } catch (e) {
      throw new Error('Invalid PDF filename encoding.');
    }

    if (decodedFilename.includes('..') || decodedFilename.includes('/') || decodedFilename.includes('\\')) {
      throw new Error('Directory traversal characters are not allowed.');
    }

    const regex = /^[A-Za-z0-9._-]+\.pdf$/;
    if (!regex.test(decodedFilename)) {
      throw new Error('Invalid PDF filename format.');
    }

    return decodedFilename;
  }
}
